import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Radio } from "lucide-react";

/** Genesys dial state + status badge — extracted from pages/LeadDetail.jsx (refactor Phase 3). */
export function useGenesysDial(id, { agentConnected, setLead }) {
  const [genesys, setGenesys] = useState({ configured: false, reason: "" });
  const [dialing, setDialing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/genesys/status").then((r) => {
      if (!cancelled) setGenesys(r.data || { configured: false });
    }).catch(() => {
      if (!cancelled) setGenesys({ configured: false, reason: "unreachable" });
    });
    return () => { cancelled = true; };
  }, []);

  const dial = async (phone) => {
    setDialing(true);
    try {
      // Prefer the agent's manual (PKCE-authenticated) dial when they have
      // connected their own Genesys session. Fall back to the tenant-level
      // contact-list push otherwise.
      const endpoint = agentConnected
        ? `/leads/${id}/dial-manual`
        : `/leads/${id}/dial`;
      const { data } = await api.post(endpoint, { phone_e164: phone });
      if (data?.lead) setLead(data.lead);
      const marker = agentConnected
        ? (data?.conversation_id ? ` · conv ${String(data.conversation_id).slice(0, 8)}` : "")
        : (data?.genesys_contact_id ? ` · contact ${String(data.genesys_contact_id).slice(0, 8)}` : "");
      toast.success(`Dial initiated${marker}`);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Dial failed";
      if (status === 424) toast.error(`Genesys not configured — ${detail}`);
      else if (status === 428) toast.warning(detail);
      else if (status === 429) toast.warning(detail);
      else if (status === 409) toast.warning(detail);
      else toast.error(detail);
    } finally {
      setDialing(false);
    }
  };

  return { genesys, dialing, dial };
}

export function GenesysBadge({ status }) {
  const ok = !!status?.configured;
  return (
    <div
      className={`flex items-center gap-1.5 px-2 h-7 rounded text-[10px] font-medium border ${
        ok
          ? "bg-emerald-50 text-emerald-700 border-emerald-200"
          : "bg-slate-50 text-slate-500 border-slate-200"
      }`}
      title={status?.reason || ""}
      data-testid="genesys-badge"
    >
      <Radio className={`w-3 h-3 ${ok ? "text-emerald-500" : "text-slate-400"}`} />
      Genesys: {ok ? "Live" : "Not configured"}
    </div>
  );
}

